import { useCallback } from "react";

import {
  CME_OPTIONSET_ACCESSORIES,
  CME_OPTIONSET_COLOUR,
  CME_OPTIONSET_ENGINE,
  CME_OPTIONSET_GRADE,
  CME_OPTIONSET_MVOB,
  CME_OPTIONSET_TRANSMISSION,
  CME_OPTIONSET_TRIM,
} from "../Data/static";
import { useCmeContext } from "../fe-cme/CMEProvider";
import { useLocation } from "@rotorstudios/fe-modules";
import { useDataContext } from "./DataProvider";

const VITE_BUILD_ENV = import.meta.env.VITE_BUILD_ENV;
const VITE_SUBDOMAIN_ENV = import.meta.env.VITE_SUBDOMAIN_ENV;
const VITE_APP_BASENAME = import.meta.env.VITE_APP_BASENAME;
const VITE_AR_URL = import.meta.env.VITE_AR_URL;

const useUrlParamLinks = () => {
  const location = useLocation();
  const { urlParams, dealershipData, vehicleData, materialCode, cmeSiloUiData } =
    useDataContext();
  const { cmeOptions } = useCmeContext();

  // ********************
  // Base URL
  // ====================

  const getBaseUrl = useCallback(() => {
    const url = new URL(globalThis.window.location.origin);
    const baseName = VITE_APP_BASENAME ?? "";
    const pathName = location.pathname;
    url.pathname = `${baseName}${pathName}`;
    return url.href;
  }, [location]);

  const getDealerBaseUrl = useCallback((dealerUrl?: string) => {
    const origin =
      VITE_SUBDOMAIN_ENV === "prod" && dealerUrl
        ? dealerUrl
        : globalThis.window.location.origin;
    const url = new URL(origin);
    url.pathname = globalThis.window.location.pathname;
    return url.href;
  }, []);

  // ********************
  // URL Params helpers
  // ====================
  const getPinUrlParams = useCallback((pin: string) => {
    const newParams = new URLSearchParams();
    newParams.set("pin", pin);
    return newParams.toString();
  }, []);

  const getDealerUrlParams = useCallback(
    (sapCode: string) => {
      const newParams = new URLSearchParams();
      if (urlParams.vehicle) {
        newParams.set("vehicle", urlParams.vehicle);
      }
      newParams.set("SC", sapCode);
      if (urlParams.MC) {
        newParams.set("MC", urlParams.MC);
      }
      if (urlParams.accessories) {
        newParams.set("accessories", urlParams.accessories);
      }
      return newParams.toString();
    },
    [urlParams],
  );

  const getVehicleUrlParams = useCallback(
    (vehicle: string) => {
      const newParams = new URLSearchParams();
      newParams.set("vehicle", vehicle);
      if (urlParams.SC) {
        newParams.set("SC", urlParams.SC);
      }
      return newParams.toString();
    },
    [urlParams],
  );

  const getConfigurationUrlParams = useCallback(
    (matCode: string, accessories?: string[]) => {
      const newParams = new URLSearchParams();
      if (urlParams.vehicle) {
        newParams.set("vehicle", urlParams.vehicle);
      }
      if (urlParams.SC) {
        newParams.set("SC", urlParams.SC);
      }
      if (urlParams.MC) {
        newParams.set("MC", matCode);
      }
      if (urlParams.accessories && accessories && accessories?.length > 0) {
        newParams.set("accessories", accessories.join(","));
      }
      return newParams.toString();
    },
    [urlParams],
  );

  // ********************
  // Link helpers
  // ====================
  const createTFYLink = useCallback(() => {
    const { selectedOptions } = cmeOptions;
    const userLink = new URL(globalThis.window.location.origin);

    userLink.pathname = VITE_APP_BASENAME ?? "";
    userLink.searchParams.append("vehicle", vehicleData?.vehicleId);
    userLink.searchParams.append("SC", dealershipData?.sapCode);
    userLink.searchParams.append("MC", materialCode?.config);

    const accessories = selectedOptions?.get(CME_OPTIONSET_ACCESSORIES);
    if (Array.isArray(accessories) && accessories.length > 0) {
      userLink.searchParams.append("accessories", accessories.join(","));
    }

    return userLink.href;
  }, [cmeOptions, vehicleData, dealershipData, materialCode]);

  const createXRSiloID = useCallback(() => {
    if (!cmeSiloUiData || !vehicleData) return "";
    return `toyota_${vehicleData.siloId}`;
  }, [cmeSiloUiData, vehicleData]);

  const createIntentUrlLink = useCallback(() => {
    const { selectedOptions } = cmeOptions;
    const userLink = new URL(VITE_AR_URL);
    const url = new URL(globalThis.window.location.href);

    const xrSiloId = createXRSiloID();

    let selectedOptList = [
      selectedOptions?.get(CME_OPTIONSET_COLOUR),
      selectedOptions?.get(CME_OPTIONSET_ENGINE),
      selectedOptions?.get(CME_OPTIONSET_GRADE),
      selectedOptions?.get(CME_OPTIONSET_TRIM),
      selectedOptions?.get(CME_OPTIONSET_MVOB),
      selectedOptions?.get(CME_OPTIONSET_TRANSMISSION),
    ];

    selectedOptList = selectedOptList.concat(
      selectedOptions?.get(CME_OPTIONSET_ACCESSORIES),
    );

    const linkBackUrl = createTFYLink();
    const encodedLinkBackUrl = encodeURIComponent(linkBackUrl);
    const selectedOptString = selectedOptList.filter(Boolean).join(",");
    const encodedSelectedOptString = encodeURIComponent(selectedOptString);

    userLink.pathname = xrSiloId;
    userLink.searchParams.append("options", encodedSelectedOptString);
    userLink.searchParams.append("linkout", encodedLinkBackUrl);

    if (url.searchParams.get("stream") === "rotorDev") {
      VITE_BUILD_ENV === "dev" && userLink.searchParams.append("stream", "rotorDev");
    }

    return userLink.href;
  }, [cmeOptions, createXRSiloID, createTFYLink]);

  const createTestDriveLink = useCallback(
    () => `${dealershipData?.nviWebsite}/test-drive-booking/${materialCode?.config}`,
    [dealershipData?.nviWebsite, materialCode],
  );

  return {
    getBaseUrl,
    getDealerBaseUrl,
    //
    getPinUrlParams,
    getDealerUrlParams,
    getVehicleUrlParams,
    getConfigurationUrlParams,
    //
    createTFYLink,
    createIntentUrlLink,
    createTestDriveLink,
  };
};

export default useUrlParamLinks;
